import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ImageBatchService} from '../../services/image-batch.service';

interface PurposeConfig {
    emptyImage?: string;
    placeholderClass: string;
    icon?: string;
    /** Show a plain circle (sized by the parent .symbol wrapper) instead of an icon/initials. */
    emptyCircle?: boolean;
}

const PURPOSE_CONFIG: Record<string, PurposeConfig> = {
    website:         { placeholderClass: 'app-image-placeholder--website',        emptyCircle: true },
    brand:           { placeholderClass: 'app-image-placeholder--brand' },
    partner:         { placeholderClass: 'app-image-placeholder--partner' },
    account:         { placeholderClass: 'app-image-placeholder--account',        icon: 'fas fa-user-lock' },
    campaign:        { placeholderClass: 'app-image-placeholder--campaign',       icon: 'fa fa-bullhorn' },
    'aff-system':    { placeholderClass: 'app-image-placeholder--aff-system' },
    platform:        { placeholderClass: 'app-image-placeholder--platform',       icon: 'fas fa-network-wired' },
    golink:          { placeholderClass: 'app-image-placeholder--golink',         icon: 'fas fa-fw fa-link' },
    game:            { placeholderClass: 'app-image-placeholder--game' },
    'game-provider': { placeholderClass: 'app-image-placeholder--game-provider' },
    lead:            { placeholderClass: 'app-image-placeholder--lead',           icon: 'fas fa-bullseye' },
    initiative:      { placeholderClass: 'app-image-placeholder--initiative',     icon: 'fas fa-fw fa-lightbulb' },
    publisher:       { placeholderClass: 'app-image-placeholder--publisher' },
    'publisher-group': { placeholderClass: 'app-image-placeholder--publisher-group' },
};

@Component({
    selector: 'app-image',
    templateUrl: './app-image.component.html',
    styleUrls: ['./app-image.component.scss']
})

export class AppImageComponent implements OnInit, OnChanges {
    @Input() imageSrc: string;
    @Input() imagePurpose: string;
    @Input() initials: string;
    @Input() initialsSize: string = 'sm';
    @Input() initialsClass: string = '';
    @Input() isAdmin: boolean = false;
    @Input() class: string;
    @Input() style: string;
    @Input() handleError: () => void;
    /**
     * Optional self-contained size preset ('sm' = 25px, 'md' = 35px). When set, the component sizes
     * and circularly clips its own image/placeholder — no external `.symbol.symbol-Npx` wrapper needed.
     * Leave unset to keep the legacy behavior (size fully controlled by an external wrapper).
     */
    @Input() size: 'sm' | 'md' | undefined;

    src: string | null

    emptyImageURL: string = "./assets/media/logos/no_image_logo.webp";
    imagePlaceholderURL: string = "./assets/media/logos/picture-icon.png";
    brokenImageURL : string = './assets/media/logos/broken-link_318-891663.avif';

    fetching: boolean = false;
    fetched: boolean = false;
    broken: boolean = false;

    delayDuration: number = 1000;
    delayPassed: boolean = false;

    maxDelayDuration: number = 5000;
    maxDelayDurationPassed: boolean = false;

    private static imageCache = new Map<string, string>();

    constructor(private imageBatchService: ImageBatchService) {}

    ngOnInit() {
        setTimeout(() => {
            this.delayPassed = true;
        }, this.delayDuration);

        setTimeout(() => {
            this.maxDelayDurationPassed = true;
        }, this.maxDelayDuration);

        this.fetchImage();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['imageSrc'] && !changes['imageSrc'].firstChange) {
            this.src = null;
            this.broken = false;
            this.fetched = false;
            this.fetching = false;
            this.fetchImage();
        }
    }

    handleImageError(event: Event) {
        const imgElement = event.target as HTMLImageElement;

        if (this.imagePurpose === 'user' && this.initials) {
            this.broken = true;
            return;
        }

        const config = PURPOSE_CONFIG[this.imagePurpose];
        if (config && (config.icon || config.emptyCircle || this.initials)) {
            this.broken = true;
            return;
        }

        if (this.isEmptySource()) {
            imgElement.src = this.emptyImageURL;
            return;
        }

        imgElement.src = this.brokenImageURL;
        imgElement.onerror = null; // Remove the event listener to prevent console error
    }

    shouldShowInitials(): boolean {
        if (this.imagePurpose === 'user') {
            return !!this.initials && (this.isEmptySource() || this.broken);
        }
        const config = PURPOSE_CONFIG[this.imagePurpose];
        if (!config) return false;
        return (!!config.icon || !!config.emptyCircle || !!this.initials) && (this.isEmptySource() || this.broken);
    }

    isEmptyCircle(): boolean {
        return !!PURPOSE_CONFIG[this.imagePurpose]?.emptyCircle;
    }

    getPurposeIcon(): string | undefined {
        return PURPOSE_CONFIG[this.imagePurpose]?.icon;
    }

    getPurposeClass(): string {
        return PURPOSE_CONFIG[this.imagePurpose]?.placeholderClass || '';
    }

    isEmptySource() {
        return !this.imageSrc || this.imageSrc.endsWith("null") || this.imageSrc === this.emptyImageURL;
    }

    get sizeClass(): string {
        return this.size ? 'app-image--' + this.size : '';
    }

    getClasses() {
        let classes = this.class;

        if (classes === undefined) {
            classes = "default";
        }

        if (this.fetched) {
            return `${classes} fade-in`
        }

        return classes;
    }

    getSrc() {
        return this.src;
    }

    getStyles() {
        if (this.style === undefined) {
            return "";
        }

        return this.style;
    }

    fetchImage() {
        if (this.isEmptySource()) {
            this.setEmptyImageUrl();
            this.fetching = false;
            this.fetched = true;
            return;
        }

        const cached = AppImageComponent.imageCache.get(this.imageSrc);
        if (cached) {
            this.src = cached;
            this.fetching = false;
            this.fetched = true;
            return;
        }

        this.fetching = true;
        this.fetched = false;

        this.imageBatchService.requestImage(this.imageSrc).subscribe({
            next: (response) => {
                this.fetching = false;
                this.fetched = true;

                if (response.success) {
                    this.setSrc(response.imageBase64!);
                } else if (this.imagePurpose === 'user' && this.initials) {
                    this.broken = true;
                } else {
                    const config = PURPOSE_CONFIG[this.imagePurpose];
                    if (config && (config.icon || config.emptyCircle || this.initials)) {
                        this.broken = true;
                    } else {
                        this.src = this.brokenImageURL;
                    }
                }
            },
            error: () => {
                this.fetching = false;
                this.fetched = true;
            }
        });
    }

    getImageFormat(url: string): string {
        const extension = url.substring(url.lastIndexOf('.') + 1).toLowerCase();

        // Map image extensions to their formats
        const formatMap: { [key: string]: string } = {
            jpg: 'jpeg',
            jpeg: 'jpeg',
            png: 'png',
            webp: 'webp',
            // Add more formats as needed
        };

        return formatMap[extension] || 'jpeg'; // Default to 'jpeg' if not found
    }

    setSrc(imageBase64: string) {
        if (this.isEmptySource()) {
            this.setEmptyImageUrl();
            return;
        }

        this.src = 'data:image/' + this.getImageFormat(this.imageSrc) + ';base64,' + imageBase64;
        AppImageComponent.imageCache.set(this.imageSrc, this.src);
    }

    setEmptyImageUrl() {
        if (!this.imagePurpose) {
            this.emptyImageURL = "./assets/media/logos/no_image_logo.webp";
            return;
        }

        if (this.imagePurpose === "user") {
            this.emptyImageURL = "./assets/media/avatars/blank.png";
        }
    }
}
